/**
 * Scene: the serializable visual state. It changes only through commands (ADR-0008), which keeps
 * presets, undo, sharing, MIDI and remote control simple.
 *
 * v2: a stack of layers (each a visualization with its own params, blend mode and opacity), a
 * palette (built-in id or inline custom stops), post-effects, modulations, LFOs, a transition
 * spec, and preset-scoped settings.
 */
import {
  CURVES,
  LFO_SHAPES,
  NUM_LFOS,
  type Curve,
  type Lfo,
  type Modulation,
  type OscSpec,
} from './modulation';
import type { ParamDef, ParamValues } from './params';
import { POSTFX } from './postfx';

export const SCENE_SCHEMA_VERSION = 2;

export type BlendMode =
  | 'normal'
  | 'add'
  | 'screen'
  | 'multiply'
  | 'overlay'
  | 'difference'
  | 'lighten'
  | 'darken'
  | 'over';
/**
 * Order matters: the index is the compositor's mode number. 'over' treats black as transparent,
 * so an element visualization (bars, particles…) keeps its own colours on top of a background.
 */
export const BLEND_MODES: readonly BlendMode[] = [
  'normal',
  'add',
  'screen',
  'multiply',
  'overlay',
  'difference',
  'lighten',
  'darken',
  'over',
];

export const BLEND_LABELS: Record<BlendMode, string> = {
  normal: 'Normal',
  add: 'Add',
  screen: 'Screen',
  multiply: 'Multiply',
  overlay: 'Overlay',
  difference: 'Difference',
  lighten: 'Lighten',
  darken: 'Darken',
  over: 'Over (black = clear)',
};

export interface Layer {
  id: string;
  vizId: string;
  params: ParamValues;
  blend: BlendMode;
  opacity: number;
  enabled: boolean;
  /** This layer's own palette; absent = the scene's (overall) palette. */
  palette?: PaletteRef;
  /** Hue shift of the layer's own palette. */
  paletteShift?: number;
  /**
   * Effects on this layer only, applied before it's blended in (keyed like Scene.postfx). The
   * scene's overall effects still apply to the finished picture on top.
   */
  postfx?: Record<string, ParamValues>;
  /** Where and how sharply the layer is shown (VIEW_PARAMS); absent values are the defaults. */
  view?: ParamValues;
}

/**
 * Controls every visualization has: framing and sharpness, applied to the layer's picture after
 * it's drawn (so they work the same for built-ins, shaders and MilkDrop).
 */
export const VIEW_PARAMS: readonly ParamDef[] = [
  { id: 'panX', label: 'Pan left / right', type: 'float', min: -1, max: 1, default: 0, step: 0.01 },
  { id: 'panY', label: 'Pan down / up', type: 'float', min: -1, max: 1, default: 0, step: 0.01 },
  { id: 'zoom', label: 'Zoom', type: 'float', min: 0.25, max: 4, default: 1, step: 0.01 },
  { id: 'sharp', label: 'Sharpness', type: 'float', min: -1, max: 1, default: 0, step: 0.01 },
];

/** A layer's view value (its own, or the default). */
export function viewValue(layer: Pick<Layer, 'view'>, id: string): number {
  return layer.view?.[id] ?? VIEW_PARAMS.find((p) => p.id === id)?.default ?? 0;
}

export interface PaletteRef {
  id: string;
  /** Inline stops for custom palettes, so shared presets carry their colours with them. */
  stops?: string[];
  cyclic?: boolean;
  name?: string;
}

export type TransitionType = 'cut' | 'crossfade' | 'wipe' | 'zoom' | 'luma' | 'morph';
export const TRANSITION_TYPES: readonly TransitionType[] = [
  'cut',
  'crossfade',
  'wipe',
  'zoom',
  'luma',
  'morph',
];
export type Quantize = 'immediate' | 'beat' | 'bar' | 'phrase' | 'drop';
export const QUANTIZE_OPTIONS: readonly Quantize[] = ['immediate', 'beat', 'bar', 'phrase', 'drop'];

export interface TransitionSpec {
  type: TransitionType;
  duration: number;
  unit: 'ms' | 'beats';
  quantize: Quantize;
}

/** Settings that travel with a preset (registry scope 'preset'). */
export interface PresetSettings {
  intensity?: number;
  beatReact?: number;
  smoothing?: number;
}

export interface Scene {
  schemaVersion: number;
  name: string;
  tags: string[];
  layers: Layer[];
  palette: PaletteRef;
  paletteShift: number;
  postfx: Record<string, ParamValues>;
  modulations: Modulation[];
  lfos: Lfo[];
  transition: TransitionSpec;
  settings: PresetSettings;
}

export const DEFAULT_TRANSITION: TransitionSpec = {
  type: 'crossfade',
  duration: 2,
  unit: 'beats',
  quantize: 'beat',
};

export function defaultLfos(): Lfo[] {
  return Array.from({ length: NUM_LFOS }, (_, i) => ({
    id: `lfo${i + 1}`,
    shape: (['sine', 'triangle', 'saw', 'random'] as const)[i]!,
    rate: [4, 8, 16, 1][i]!,
    sync: true,
  }));
}

export function newScene(vizId = 'radial-pulse', layerId = 'l1'): Scene {
  return {
    schemaVersion: SCENE_SCHEMA_VERSION,
    name: 'Untitled',
    tags: [],
    layers: [{ id: layerId, vizId, params: {}, blend: 'normal', opacity: 1, enabled: true }],
    palette: { id: 'neon-night' },
    paletteShift: 0,
    postfx: {},
    modulations: [],
    lfos: defaultLfos(),
    transition: { ...DEFAULT_TRANSITION },
    settings: {},
  };
}

// ---- commands

export type SceneCommand =
  | { type: 'scene.rename'; name: string }
  | { type: 'scene.tags'; tags: string[] }
  | { type: 'layer.add'; layer: Layer; index?: number }
  | { type: 'layer.remove'; layerId: string }
  | { type: 'layer.move'; layerId: string; delta: number }
  | { type: 'layer.set'; layerId: string; patch: Partial<Omit<Layer, 'id' | 'params'>> }
  | { type: 'layer.param'; layerId: string; paramId: string; value: number }
  | { type: 'layer.resetParams'; layerId: string }
  | { type: 'layer.view'; layerId: string; key: string; value: number }
  | { type: 'layer.resetView'; layerId: string }
  | { type: 'layer.palette'; layerId: string; palette: PaletteRef | null }
  | { type: 'layer.paletteShift'; layerId: string; value: number }
  | { type: 'layer.fx.param'; layerId: string; fxId: string; paramId: string; value: number }
  /** Remove the layer's own copy of an effect (it then just gets the overall one). */
  | { type: 'layer.fx.reset'; layerId: string; fxId: string }
  | { type: 'palette.set'; palette: PaletteRef }
  | { type: 'palette.shift'; value: number }
  | { type: 'fx.param'; fxId: string; paramId: string; value: number }
  | { type: 'fx.reset'; fxId: string }
  | { type: 'mod.add'; modulation: Modulation }
  | { type: 'mod.update'; id: string; patch: Partial<Omit<Modulation, 'id'>> }
  | { type: 'mod.remove'; id: string }
  | { type: 'lfo.update'; id: string; patch: Partial<Omit<Lfo, 'id'>> }
  | { type: 'transition.set'; patch: Partial<TransitionSpec> }
  | { type: 'setting.set'; key: keyof PresetSettings; value: number | undefined };

const wrap01 = (v: number) => ((v % 1) + 1) % 1;

function mapLayer(scene: Scene, id: string, f: (l: Layer) => Layer): Scene {
  return { ...scene, layers: scene.layers.map((l) => (l.id === id ? f(l) : l)) };
}

export function applySceneCommand(scene: Scene, cmd: SceneCommand): Scene {
  switch (cmd.type) {
    case 'scene.rename':
      return { ...scene, name: cmd.name.slice(0, 80) };
    case 'scene.tags':
      return { ...scene, tags: cmd.tags.slice(0, 16) };
    case 'layer.add': {
      if (scene.layers.length >= MAX_LAYERS) return scene;
      const layers = [...scene.layers];
      layers.splice(cmd.index ?? layers.length, 0, cmd.layer);
      return { ...scene, layers };
    }
    case 'layer.remove': {
      if (scene.layers.length <= 1) return scene;
      const prefix = `layer/${cmd.layerId}/`;
      return {
        ...scene,
        layers: scene.layers.filter((l) => l.id !== cmd.layerId),
        modulations: scene.modulations.filter((m) => !m.target.startsWith(prefix)),
      };
    }
    case 'layer.move': {
      const i = scene.layers.findIndex((l) => l.id === cmd.layerId);
      const j = i + cmd.delta;
      if (i < 0 || j < 0 || j >= scene.layers.length) return scene;
      const layers = [...scene.layers];
      const [l] = layers.splice(i, 1);
      layers.splice(j, 0, l!);
      return { ...scene, layers };
    }
    case 'layer.set':
      return mapLayer(scene, cmd.layerId, (l) => {
        const next = { ...l, ...cmd.patch };
        // A different visualization has different parameters.
        if (cmd.patch.vizId && cmd.patch.vizId !== l.vizId) next.params = {};
        return next;
      });
    case 'layer.param':
      return mapLayer(scene, cmd.layerId, (l) => ({
        ...l,
        params: { ...l.params, [cmd.paramId]: cmd.value },
      }));
    case 'layer.resetParams':
      return mapLayer(scene, cmd.layerId, (l) => ({ ...l, params: {} }));
    case 'layer.view': {
      const def = VIEW_PARAMS.find((p) => p.id === cmd.key);
      if (!def || !Number.isFinite(cmd.value)) return scene;
      const value = Math.min(def.max, Math.max(def.min, cmd.value));
      return mapLayer(scene, cmd.layerId, (l) => {
        const view = { ...l.view };
        if (Math.abs(value - def.default) < 1e-6) delete view[def.id];
        else view[def.id] = value;
        const next: Layer = { ...l, view };
        if (Object.keys(view).length === 0) delete next.view;
        return next;
      });
    }
    case 'layer.resetView':
      return mapLayer(scene, cmd.layerId, (l) => {
        const next = { ...l };
        delete next.view;
        return next;
      });
    case 'layer.palette':
      return mapLayer(scene, cmd.layerId, (l) => {
        const next = { ...l };
        if (cmd.palette) next.palette = cmd.palette;
        else {
          delete next.palette;
          delete next.paletteShift;
        }
        return next;
      });
    case 'layer.paletteShift':
      return mapLayer(scene, cmd.layerId, (l) => ({ ...l, paletteShift: wrap01(cmd.value) }));
    case 'layer.fx.param':
      return mapLayer(scene, cmd.layerId, (l) => ({
        ...l,
        postfx: {
          ...l.postfx,
          [cmd.fxId]: { ...l.postfx?.[cmd.fxId], [cmd.paramId]: cmd.value },
        },
      }));
    case 'layer.fx.reset':
      return mapLayer(scene, cmd.layerId, (l) => {
        if (!l.postfx?.[cmd.fxId]) return l;
        const postfx = { ...l.postfx };
        delete postfx[cmd.fxId];
        const next: Layer = { ...l, postfx };
        if (Object.keys(postfx).length === 0) delete next.postfx;
        return next;
      });
    case 'palette.set':
      return { ...scene, palette: cmd.palette };
    case 'palette.shift':
      return { ...scene, paletteShift: wrap01(cmd.value) };
    case 'fx.param':
      return {
        ...scene,
        postfx: {
          ...scene.postfx,
          [cmd.fxId]: { ...scene.postfx[cmd.fxId], [cmd.paramId]: cmd.value },
        },
      };
    case 'fx.reset': {
      const postfx = { ...scene.postfx };
      delete postfx[cmd.fxId];
      return { ...scene, postfx };
    }
    case 'mod.add':
      if (scene.modulations.length >= MAX_MODULATIONS) return scene;
      return { ...scene, modulations: [...scene.modulations, cmd.modulation] };
    case 'mod.update':
      return {
        ...scene,
        modulations: scene.modulations.map((m) => (m.id === cmd.id ? { ...m, ...cmd.patch } : m)),
      };
    case 'mod.remove':
      return { ...scene, modulations: scene.modulations.filter((m) => m.id !== cmd.id) };
    case 'lfo.update':
      return {
        ...scene,
        lfos: scene.lfos.map((l) => (l.id === cmd.id ? { ...l, ...cmd.patch } : l)),
      };
    case 'transition.set':
      return { ...scene, transition: { ...scene.transition, ...cmd.patch } };
    case 'setting.set': {
      const settings = { ...scene.settings };
      if (cmd.value === undefined) delete settings[cmd.key];
      else settings[cmd.key] = cmd.value;
      return { ...scene, settings };
    }
  }
}

export const MAX_LAYERS = 6;
export const MAX_MODULATIONS = 64;

// ---- modulation targets

export interface TargetInfo {
  min: number;
  max: number;
  base: number;
  label: string;
}

export type VizLookup = (
  vizId: string,
) => { name: string; params: readonly ParamDef[] } | undefined;

/** Range and current (unmodulated) value of a modulation target, or null if it no longer exists. */
export function resolveTarget(scene: Scene, target: string, viz: VizLookup): TargetInfo | null {
  const parts = target.split('/');
  if (parts[0] === 'layer' && parts.length >= 4) {
    const layer = scene.layers.find((l) => l.id === parts[1]);
    if (!layer) return null;
    const name = viz(layer.vizId)?.name ?? layer.vizId;
    if (parts[2] === 'palette' && parts[3] === 'shift' && parts.length === 4)
      return { min: 0, max: 1, base: layer.paletteShift ?? 0, label: `${name} · Hue shift` };
    if (parts[2] === 'view' && parts.length === 4) {
      const p = VIEW_PARAMS.find((x) => x.id === parts[3]);
      if (!p) return null;
      return {
        min: p.min,
        max: p.max,
        base: viewValue(layer, p.id),
        label: `${name} · ${p.label}`,
      };
    }
    if (parts[2] === 'fx' && parts.length === 5) {
      const fx = POSTFX.find((f) => f.id === parts[3]);
      const p = fx?.params.find((x) => x.id === parts[4]);
      if (!fx || !p) return null;
      return {
        min: p.min,
        max: p.max,
        base: layer.postfx?.[fx.id]?.[p.id] ?? p.default,
        label: `${name} · ${fx.name} · ${p.label}`,
      };
    }
    return null;
  }
  if (parts[0] === 'layer' && parts.length === 3) {
    const layer = scene.layers.find((l) => l.id === parts[1]);
    if (!layer) return null;
    const def = viz(layer.vizId);
    if (parts[2] === 'opacity') {
      return {
        min: 0,
        max: 1,
        base: layer.opacity,
        label: `${def?.name ?? layer.vizId} · Opacity`,
      };
    }
    const p = def?.params.find((x) => x.id === parts[2]);
    if (!p) return null;
    return {
      min: p.min,
      max: p.max,
      base: layer.params[p.id] ?? p.default,
      label: `${def!.name} · ${p.label}`,
    };
  }
  if (parts[0] === 'fx' && parts.length === 3) {
    const fx = POSTFX.find((f) => f.id === parts[1]);
    const p = fx?.params.find((x) => x.id === parts[2]);
    if (!fx || !p) return null;
    return {
      min: p.min,
      max: p.max,
      base: scene.postfx[fx.id]?.[p.id] ?? p.default,
      label: `${fx.name} · ${p.label}`,
    };
  }
  if (target === 'palette/shift')
    return { min: 0, max: 1, base: scene.paletteShift, label: 'Palette · Hue shift' };
  if (target === 'master/intensity') {
    return { min: 0, max: 2, base: scene.settings.intensity ?? 1, label: 'Master · Intensity' };
  }
  return null;
}

// ---- validation & migration of untrusted input (storage, files, share links)

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const str = (v: unknown, fallback: string, max = 80) =>
  typeof v === 'string' ? v.slice(0, max) : fallback;
const HEX = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i;

function cleanView(v: unknown): ParamValues {
  const out: ParamValues = {};
  if (!v || typeof v !== 'object') return out;
  for (const p of VIEW_PARAMS) {
    const x = (v as Record<string, unknown>)[p.id];
    if (isNum(x) && Math.abs(x - p.default) > 1e-6) out[p.id] = Math.min(p.max, Math.max(p.min, x));
  }
  return out;
}

function cleanValues(v: unknown): ParamValues {
  const out: ParamValues = {};
  if (v && typeof v === 'object') {
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (isNum(x) && k.length < 64) out[k] = x;
    }
  }
  return out;
}

function cleanOsc(v: unknown): { osc?: OscSpec } {
  if (!v || typeof v !== 'object') return {};
  const o = v as Record<string, unknown>;
  return {
    osc: {
      shape: LFO_SHAPES.includes(o.shape as Lfo['shape']) ? (o.shape as Lfo['shape']) : 'sine',
      rate: isNum(o.rate) ? Math.max(0.01, Math.min(64, o.rate)) : 4,
      sync: o.sync !== false,
    },
  };
}

function cleanPalette(v: unknown): PaletteRef {
  const p = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const palette: PaletteRef = { id: str(p.id, 'neon-night', 64) };
  if (Array.isArray(p.stops)) {
    const stops = p.stops
      .filter((s): s is string => typeof s === 'string' && HEX.test(s))
      .slice(0, 16);
    if (stops.length >= 2) palette.stops = stops;
  }
  if (p.cyclic === true) palette.cyclic = true;
  if (typeof p.name === 'string') palette.name = p.name.slice(0, 40);
  return palette;
}

function cleanPostfx(v: unknown): Record<string, ParamValues> {
  const postfx: Record<string, ParamValues> = {};
  if (v && typeof v === 'object') {
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (POSTFX.some((f) => f.id === k)) postfx[k] = cleanValues(x);
    }
  }
  return postfx;
}

/** Migrate a v1 scene (one visualization, per-viz params) to v2. */
export function migrateV1(o: Record<string, unknown>): Record<string, unknown> {
  const vizId = typeof o.vizId === 'string' ? o.vizId : 'radial-pulse';
  const params = (o.params as Record<string, unknown> | undefined)?.[vizId];
  return {
    ...newScene(vizId),
    layers: [
      { id: 'l1', vizId, params: cleanValues(params), blend: 'normal', opacity: 1, enabled: true },
    ],
    palette: { id: typeof o.paletteId === 'string' ? o.paletteId : 'neon-night' },
    paletteShift: o.paletteShift,
  };
}

/** Validate (and migrate) an untrusted scene. Unknown visualization ids are kept; the engine falls back. */
export function sanitizeScene(input: unknown, fallback: Scene): Scene {
  if (!input || typeof input !== 'object') return fallback;
  let o = input as Record<string, unknown>;
  if (o.schemaVersion === 1) o = migrateV1(o);
  if (o.schemaVersion !== SCENE_SCHEMA_VERSION) return fallback;

  const layers: Layer[] = [];
  if (Array.isArray(o.layers)) {
    for (const raw of o.layers.slice(0, MAX_LAYERS)) {
      if (!raw || typeof raw !== 'object') continue;
      const l = raw as Record<string, unknown>;
      if (typeof l.vizId !== 'string') continue;
      const layer: Layer = {
        id: str(l.id, `l${layers.length + 1}`, 32),
        vizId: l.vizId.slice(0, 64),
        params: cleanValues(l.params),
        blend: BLEND_MODES.includes(l.blend as BlendMode) ? (l.blend as BlendMode) : 'normal',
        opacity: isNum(l.opacity) ? Math.min(1, Math.max(0, l.opacity)) : 1,
        enabled: l.enabled !== false,
      };
      if (l.palette && typeof l.palette === 'object') layer.palette = cleanPalette(l.palette);
      if (layer.palette && isNum(l.paletteShift))
        layer.paletteShift = ((l.paletteShift % 1) + 1) % 1;
      const fx = cleanPostfx(l.postfx);
      if (Object.keys(fx).length) layer.postfx = fx;
      const view = cleanView(l.view);
      if (Object.keys(view).length) layer.view = view;
      layers.push(layer);
    }
  }
  if (layers.length === 0) return fallback;

  const palette = cleanPalette(o.palette ?? {});
  const postfx = cleanPostfx(o.postfx);

  const modulations: Modulation[] = [];
  if (Array.isArray(o.modulations)) {
    for (const raw of o.modulations.slice(0, MAX_MODULATIONS)) {
      if (!raw || typeof raw !== 'object') continue;
      const m = raw as Record<string, unknown>;
      if (typeof m.target !== 'string' || typeof m.source !== 'string') continue;
      modulations.push({
        id: str(m.id, `m${modulations.length + 1}`, 32),
        target: m.target.slice(0, 128),
        source: m.source.slice(0, 64),
        amount: isNum(m.amount) ? Math.max(-1, Math.min(1, m.amount)) : 0.5,
        curve: CURVES.includes(m.curve as Curve) ? (m.curve as Curve) : 'linear',
        attackMs: isNum(m.attackMs) ? Math.max(0, Math.min(5000, m.attackMs)) : 10,
        releaseMs: isNum(m.releaseMs) ? Math.max(0, Math.min(5000, m.releaseMs)) : 150,
        ...cleanOsc(m.osc),
        ...(m.bipolar === true ? { bipolar: true } : {}),
      });
    }
  }

  const lfos = defaultLfos().map((d) => {
    const raw = Array.isArray(o.lfos)
      ? (o.lfos as unknown[]).find((x) => (x as Lfo)?.id === d.id)
      : undefined;
    if (!raw || typeof raw !== 'object') return d;
    const l = raw as Record<string, unknown>;
    return {
      id: d.id,
      shape: LFO_SHAPES.includes(l.shape as Lfo['shape']) ? (l.shape as Lfo['shape']) : d.shape,
      rate: isNum(l.rate) ? Math.max(0.01, Math.min(64, l.rate)) : d.rate,
      sync: typeof l.sync === 'boolean' ? l.sync : d.sync,
    };
  });

  const t = (o.transition ?? {}) as Record<string, unknown>;
  const transition: TransitionSpec = {
    type: TRANSITION_TYPES.includes(t.type as TransitionType)
      ? (t.type as TransitionType)
      : DEFAULT_TRANSITION.type,
    duration: isNum(t.duration)
      ? Math.max(0, Math.min(64000, t.duration))
      : DEFAULT_TRANSITION.duration,
    unit: t.unit === 'ms' ? 'ms' : 'beats',
    quantize: QUANTIZE_OPTIONS.includes(t.quantize as Quantize)
      ? (t.quantize as Quantize)
      : DEFAULT_TRANSITION.quantize,
  };

  const s = (o.settings ?? {}) as Record<string, unknown>;
  const settings: PresetSettings = {};
  if (isNum(s.intensity)) settings.intensity = Math.max(0, Math.min(2, s.intensity));
  if (isNum(s.beatReact)) settings.beatReact = Math.max(0, Math.min(2, s.beatReact));
  if (isNum(s.smoothing)) settings.smoothing = Math.max(0, Math.min(1, s.smoothing));

  return {
    schemaVersion: SCENE_SCHEMA_VERSION,
    name: str(o.name, 'Untitled'),
    tags: Array.isArray(o.tags)
      ? o.tags.filter((x): x is string => typeof x === 'string').slice(0, 16)
      : [],
    layers,
    palette,
    paletteShift: isNum(o.paletteShift) ? wrap01(o.paletteShift) : 0,
    postfx,
    modulations,
    lfos,
    transition,
    settings,
  };
}

/** Short random id for layers/modulations. */
export function uid(prefix: string): string {
  return `${prefix}${Math.random().toString(36).slice(2, 8)}`;
}
